import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { useTimeStore } from "../../stores/time";
import useModals from "../../hooks/useModals";
import Time from "./Time";
import { addColor, addTimeByDay, nameShortCut } from "../../services/function";
import remote from "../../assets/icons/darkIcons/home.svg";
import { useEffect, useState } from "react";
import MenuUpdateDelete from "../../hooks/MenuUpdateDelete";
import DeleteTime from "./DeleteTime";
import { showToastError } from "../../services/toast";
import useTooltip from "../../hooks/useTooltip";
import Holidays from "./Holidays";

const WeekCalendar = () => {
  // Store
  const { weekDays, times, setIdTimeSelected } = useTimeStore();

  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();

  // Hook
  const [tooltipState, handleMouseEnter, handleMouseLeave] = useTooltip();

  // State
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    show: boolean;
    idTimeSelected: number | null;
    idEvent: number | null;
    validationDate: boolean | null;
    timesLabel: string | null;
  }>({
    x: 0,
    y: 0,
    show: false,
    idTimeSelected: null,
    idEvent: null,
    validationDate: null,
    timesLabel: null,
  });

  // Fonction pour gérer le clic droit sur un temps
  const handleContextMenu = (
    event: React.MouseEvent,
    idTimeSelected: number,
    validationDate: string | null,
    idEvent: number | null,
    timesLabel: string | null,
  ) => {
    if (validationDate !== null) {
      showToastError("Impossible de modifier / supprimer un temps validé");
      return;
    } else {
      event.preventDefault();
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        show: true,
        idTimeSelected,
        idEvent,
        validationDate,
        timesLabel,
      });
    }
  };

  const handleEdit = () => {
    // Modification d'un temps non validé
    if (contextMenu.idTimeSelected !== null && contextMenu.idEvent === null) {
      setIdTimeSelected(contextMenu.idTimeSelected);
      setContextMenu({ ...contextMenu, show: false });
      openModal("time", <Time closeModal={() => closeModal("time")} />);
    }
    // Modification d'un congé non validé
    else if (
      contextMenu.idEvent !== null &&
      contextMenu.validationDate === null
    ) {
      setIdTimeSelected(contextMenu.idEvent);
      setContextMenu({ ...contextMenu, show: false });
      openModal(
        "holidays",
        <Holidays
          closeModal={() => closeModal("holidays")}
          idEvent={Number(contextMenu.idEvent)}
        />,
      );
    }
  };

  const handleDelete = () => {
    // Suppression d'un temps non validé
    if (contextMenu.idTimeSelected !== null && contextMenu.idEvent === null) {
      setIdTimeSelected(contextMenu.idTimeSelected);
      setContextMenu({ ...contextMenu, show: false });
      openModal(
        "deleteTime",
        <DeleteTime
          closeModal={() => closeModal("deleteTime")}
          idEvent={
            contextMenu.idEvent !== null ? Number(contextMenu.idEvent) : null
          }
        />,
      );
    }
    // Suppression d'un congé non validé
    else if (
      contextMenu.idEvent !== null &&
      contextMenu.validationDate === null
    ) {
      setContextMenu({ ...contextMenu, show: false });
      openModal(
        "deleteTime",
        <DeleteTime
          closeModal={() => closeModal("deleteTime")}
          idEvent={Number(contextMenu.idEvent)}
        />,
      );
    }
  };

  // Ajoute une couleur à chaque temps
  const coloredTimes = addColor(times);

  useEffect(() => {
    addTimeByDay(coloredTimes);
  }, [times]);

  const totalTimesByDay = addTimeByDay(coloredTimes);

  return (
    <>
      {/* Calendrier */}
      <div className="grid h-full grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        {weekDays.map((day, index) => (
          <div
            className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white"
            key={index}
          >
            {/* Header des temps */}
            <div className="shrink-0 border-b border-slate-100 bg-slate-50 px-2 py-2 text-center font-Nunito text-xs font-bold text-slate-700 capitalize">
              {format(day, "EEEE dd/MM", { locale: fr })}
              <span className="mt-0.5 block text-[11px] font-semibold text-dca-blue tabular-nums">
                Total :{" "}
                {totalTimesByDay[format(day, "dd/MM/yyyy")]?.total || "00:00"}
              </span>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              <button
                className="mb-2 flex w-full cursor-pointer items-center justify-center gap-1 rounded-lg bg-indigo-50 px-2 py-1.5 font-Nunito text-xs font-bold text-indigo-700 ring-1 ring-indigo-100 ring-inset transition-colors hover:bg-indigo-100"
                onClick={() => {
                  setIdTimeSelected(0);
                  openModal(
                    "time",
                    <Time
                      closeModal={() => closeModal("time")}
                      day={format(day, "yyyy-MM-dd")}
                    />,
                  );
                }}
              >
                + Ajouter
              </button>

              {/* Liste des temps par jour */}
              <div className="flex flex-col gap-2 font-Nunito">
                {coloredTimes
                  ?.filter((time) => format(day, "dd/MM/yyyy") === time.date)
                  .map((time) => (
                    <div
                      key={time.id}
                      className="cursor-pointer rounded-xl border border-slate-200 bg-white p-2 transition-shadow hover:shadow-sm"
                      style={{ backgroundColor: time.color }}
                      onMouseEnter={(e) => {
                        if (time?.comment && time.comment.trim()) {
                          handleMouseEnter(time.comment, e);
                        }
                      }}
                      onMouseLeave={handleMouseLeave}
                      onContextMenu={(event) => {
                        setIdTimeSelected(time.id);
                        handleContextMenu(
                          event,
                          time.id,
                          time.validationDate,
                          time.idEvent,
                          time.timesLabel,
                        );
                      }}
                    >
                      <div className="flex items-center justify-between gap-1.5">
                        <p className="shrink-0 text-xs font-bold text-slate-800 tabular-nums">
                          {time.length}
                        </p>
                        {/* Affiche un check si le temps validé  */}
                        {time.validationDate !== null ? (
                          <span className="text-[11px]">✅</span>
                        ) : (
                          <div className="h-4"></div>
                        )}
                        {time.XS === true && (
                          <span className="text-[11px] font-bold text-slate-600">
                            XS
                          </span>
                        )}
                        {/* Affiche une maison si temps en remote  */}
                        {time.remote === true ? (
                          <img src={remote} alt="home" className="size-3.5" />
                        ) : null}
                      </div>
                      <p className="text-xs font-bold text-slate-800">
                        {time.administrative === true
                          ? "ADMINISTRATIF"
                          : time.customerFile !== null
                            ? nameShortCut(time.customerFile, 18)
                            : time.timesLabel}
                      </p>
                      <div className=" flex items-center justify-between gap-2">
                        <p className="min-w-0 truncate text-[11px] text-slate-600">
                          {time.administrative === true
                            ? time.timesLabel
                            : time.mission}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ))}

        {/* Permet d'afficher le menu modification / suppression */}
        {contextMenu.show && contextMenu.timesLabel !== "Jour férié" && (
          <MenuUpdateDelete
            xPos={contextMenu.x}
            yPos={contextMenu.y}
            onEdit={handleEdit}
            onDelete={handleDelete}
            closeMenu={() => setContextMenu({ ...contextMenu, show: false })}
          />
        )}

        {/* Permet d'afficher le tooltip pour le commentaire */}
        {tooltipState.visible && tooltipState.content && (
          <div
            className="fixed z-9999 max-w-[400px] -translate-x-1/2 rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 font-Nunito text-xs text-white shadow-lg"
            style={{
              top: tooltipState.position.y,
              left: tooltipState.position.x,
              pointerEvents: "none",
            }}
          >
            {tooltipState.content}
          </div>
        )}
      </div>

      {/* Conteneur des modales (séparé du calendrier) */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}
    </>
  );
};

export default WeekCalendar;
